package one.healthium.settings.ui.meal_plan

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import loco.SETTINGS_MEAL_PLAN_SETTINGS_SCREEN_TITLE
import one.healthium.loco.LString
import one.healthium.loco.resolve
import one.healthium.settings.ui.components.Section
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.components.adaptive.WindowSizeConstraintScrollableContainer
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface MealPlanSettingsEventDelegate {
    fun onBackClicked()
}

@Composable
fun MealPlanSettingsScreen(
    eventDelegate: MealPlanSettingsEventDelegate,
) {
    val viewModel = koinViewModel<MealPlanSettingsViewModel>()
    val state by viewModel.state.collectAsState()

    MealPlanSettingsScreenContent(
        state = state,
        onBackClicked = eventDelegate::onBackClicked,
        onMealPlanClicked = viewModel::onMealPlanClicked,
    )

    AlertUiMessageHandler(viewModel.messages)
}

@Composable
internal fun MealPlanSettingsScreenContent(
    state: MealPlanSettingsUiState,
    onBackClicked: () -> Unit,
    onMealPlanClicked: (MealPlanUiModel) -> Unit,
) {
    AppScaffold(
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(loco.Res.string.SETTINGS_MEAL_PLAN_SETTINGS_SCREEN_TITLE))
                },
                navigationIcon = { AppBackNavigationButton(onClick = onBackClicked) },
            )
        }
    ) {
        Box(modifier = Modifier.padding(it)) {
            if (state.uiData != null) {
                MealPlansList(
                    list = state.uiData,
                    onClick = onMealPlanClicked,
                )
            }

            AppLoadingScreen(visible = state.loading)
        }
    }
}

@Composable
private fun MealPlansList(
    modifier: Modifier = Modifier,
    list: List<Any>,
    onClick: (MealPlanUiModel) -> Unit,
) {
    WindowSizeConstraintScrollableContainer(
        additionalContentPadding = PaddingValues(top = 24.dp, bottom = 160.dp),
    ) {
        LazyColumn(
            modifier = modifier,
            contentPadding = it,
        ) {
            items(list.size) { index ->
                if (index > 0) {
                    HorizontalDivider()
                }

                when (val uiModel = list[index]) {
                    is MealPlanUiModel -> MealPlanItem(
                        uiModel = uiModel,
                        onClick = { onClick(uiModel) },
                    )

                    is LString -> Section(uiModel.resolve())

                    else -> {}
                }
            }
        }
    }
}

@Composable
private fun MealPlanItem(
    uiModel: MealPlanUiModel,
    onClick: () -> Unit = {},
) {
    Box(
        modifier = Modifier
            .background(white)
            .clickable { onClick() },
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .defaultMinSize(minHeight = 60.dp)
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                modifier = Modifier.weight(1f),
                text = uiModel.name.resolve(),
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.text100,
            )
            Spacer(Modifier.width(16.dp))
            if (uiModel.checked) {
                Image(
                    painter = painterResource(AppIcons.icCheckMarkChecked),
                    contentDescription = null,
                )
            } else {
                Spacer(Modifier.width(24.dp))
            }
        }
    }
}
